import { useEffect, useRef, useState, type CSSProperties } from "react";

export function ConnectionDiagram() {
  const diagramRef = useRef<HTMLDivElement>(null);
  const [playback, setPlayback] = useState(0);

  useEffect(() => {
    const diagram = diagramRef.current;
    if (!diagram || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setPlayback(1);
        observer.disconnect();
      }
    }, { threshold: 0.8 });
    observer.observe(diagram);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={diagramRef} className="oc-connection" role="group" aria-label="Illustration: video travels from iPhone to your computer over Wi-Fi or USB">
      <span className="oc-connection-device">iPhone</span>
      <div className="oc-connection-middle">
        <div className="oc-connection-caption">
          <span>Wi-Fi or USB</span>
          <button className="oc-connection-replay" type="button" onClick={() => setPlayback(value => value + 1)} aria-label="Replay connection animation" title="Replay animation">
            <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7a6.5 6.5 0 1 1-.4 5M4 3v4h4" /></svg>
          </button>
        </div>
        <div key={playback} className="oc-connection-track" data-play={playback > 0} aria-hidden="true">
          <div className="oc-connection-wire" />
          <span className="oc-connection-end oc-connection-source" />
          <span className="oc-connection-end oc-connection-destination" />
          {[0, 0.4, 0.8].map(delay => (
            <div key={delay} className="oc-connection-packet" style={{ "--packet-delay": `${delay}s` } as CSSProperties}>
              <span className="oc-connection-halo oc-halo-source" />
              <span className="oc-connection-halo oc-halo-destination" />
              <div className="oc-packet-flight">
                <svg className="oc-packet-picture" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <g className="oc-packet-frame">
                    <rect x="1.5" y="2.5" width="17" height="15" rx="3" fill="rgb(var(--bg-primary))" stroke="currentColor" strokeWidth="1.2" />
                    <path d="m4 14 4-4 3 3 2-2 3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="13.5" cy="7" r="1.25" fill="currentColor" />
                  </g>
                  <circle className="oc-packet-dot" cx="10" cy="10" r="2.5" fill="currentColor" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>
      <span className="oc-connection-device">Computer</span>
    </div>
  );
}
